import { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { useOnlineStatus, contarFotosPendientesBac, sincronizarFotosBac, leerFotosAtascadasBac, descartarFotoPendienteBac, limpiarFotosYaSincronizadasBac } from "../../hooks/useSync";

// A partir de este tiempo sin terminar, se avisa que puede ser la
// conexión — pero NUNCA se detiene el intento, solo se avisa.
const AVISO_TARDANZA_MS = 45 * 1000;

/**
 * Aparece solo — sin que nadie tenga que darle a ningún botón — apenas
 * el dispositivo recupera conexión a internet y hay fotos guardadas
 * localmente esperando subir (de la vivienda o de los carnets). Se
 * queda visible mientras dure la sincronización, con cuántas van y
 * cuántas faltan, y avisa (sin bloquear) si se está demorando más de lo
 * normal por una conexión lenta.
 *
 * Móntalo una sola vez en BacSarampionHome.jsx, junto a
 * EstadoSincronizacionBac.
 */
export default function SincronizandoImagenesBac() {
  const online = useOnlineStatus();
  const eraOfflineAntes = useRef(!online);

  const [activo, setActivo] = useState(false);
  const [totalInicial, setTotalInicial] = useState(0);
  const [restantes, setRestantes] = useState(0);
  const [tardando, setTardando] = useState(false);
  const [atascadas, setAtascadas] = useState([]);
  const [verDetalle, setVerDetalle] = useState(false);
  const [descartando, setDescartando] = useState(null);
  const [terminado, setTerminado] = useState(false);
  const corriendo = useRef(false);

  // Avisa en el momento — no solo por consola — cuando una foto se
  // descarta de forma definitiva (el servidor la rechazó, o la visita
  // dueña nunca se pudo guardar) o cuando lleva demasiados intentos sin
  // avanzar. Antes esto solo quedaba en la consola del navegador, nadie
  // en campo se enteraba.
  useEffect(() => {
    const alHaberError = (e) => {
      if (e.detail?.tabla !== "bac_fotos_pendientes") return;
      Swal.fire({
        icon: "warning",
        title: "Una foto no se pudo sincronizar",
        html: `${e.detail.codigo_vivienda ? `<strong>${e.detail.codigo_vivienda}</strong><br/>` : ""}${e.detail.motivo}`,
        confirmButtonColor: "#16342a",
      });
    };
    window.addEventListener("bac-error-sincronizacion", alHaberError);
    return () => window.removeEventListener("bac-error-sincronizacion", alHaberError);
  }, []);

  const iniciarSincronizacion = async () => {
    if (corriendo.current) return; // ya hay una corriendo, no duplicar
    const n = await contarFotosPendientesBac();
    if (n === 0) return;

    corriendo.current = true;
    setTotalInicial(n);
    setRestantes(n);
    setTerminado(false);
    setTardando(false);
    setActivo(true);

    const inicio = Date.now();
    const intervalo = setInterval(async () => {
      const r = await contarFotosPendientesBac();
      setRestantes(r);
      if (Date.now() - inicio > AVISO_TARDANZA_MS) setTardando(true);
      setAtascadas(await leerFotosAtascadasBac());
      if (r === 0) {
        clearInterval(intervalo);
        setTerminado(true);
        corriendo.current = false;
        setTimeout(() => setActivo(false), 2500); // se cierra sola, no hay que darle a nada
      }
    }, 1500);

    try {
      await sincronizarFotosBac();
    } catch {
      // el intervalo de arriba sigue monitoreando el avance real —
      // aunque esta llamada puntual falle, el conteo de restantes es
      // la verdad, no esta promesa.
    } finally {
      // Si terminó pero el intervalo todavía no lo detectó (última
      // vuelta), se fuerza una revisión final.
      const r = await contarFotosPendientesBac();
      setRestantes(r);
      if (r === 0) {
        clearInterval(intervalo);
        setTerminado(true);
        corriendo.current = false;
        setTimeout(() => setActivo(false), 2500);
      }
    }
  };

  // Se dispara automáticamente en el momento exacto en que vuelve la
  // conexión (transición sin-internet → con-internet).
  useEffect(() => {
    if (online && eraOfflineAntes.current) {
      iniciarSincronizacion();
    }
    eraOfflineAntes.current = !online;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  // También revisa al abrir la app — por si quedaron fotos pendientes
  // de una sesión anterior y ya hay señal desde el principio.
  useEffect(() => {
    if (online) iniciarSincronizacion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Limpieza retroactiva — una sola vez por dispositivo, sola, sin
  // avisar (son fotos que YA se sincronizaron hace tiempo, no hay
  // ningún dato en riesgo). Libera el espacio que se fue acumulando
  // antes de que existiera el borrado automático al sincronizar.
  useEffect(() => {
    limpiarFotosYaSincronizadasBac().then(r => {
      if (r.limpiadas > 0) console.log(`🧹 Liberado espacio de ${r.limpiadas} foto(s) vieja(s)`);
    }).catch(() => {});
  }, []);

  const descartarFoto = async (foto) => {
    const confirmado = await Swal.fire({
      icon: "warning",
      title: "¿Descartar esta foto?",
      html: `${foto.codigo_vivienda} — ${foto.campo === "vivienda" ? "foto de la vivienda" : "foto del carné"}<br/><br/>Lleva ${foto.intentosFallidos} intentos sin poder subir. Esta foto se pierde — si todavía hace falta, hay que volver a tomarla desde el registro.`,
      showCancelButton: true, confirmButtonText: "Sí, descartar", cancelButtonText: "Cancelar", confirmButtonColor: "#b3261e",
    });
    if (!confirmado.isConfirmed) return;
    setDescartando(foto.id);
    await descartarFotoPendienteBac(foto.id);
    setAtascadas(await leerFotosAtascadasBac());
    setRestantes(await contarFotosPendientesBac());
    setDescartando(null);
  };

  if (!activo) return null;

  const sincronizadas = totalInicial - restantes;
  const progreso = totalInicial > 0 ? Math.round((sincronizadas / totalInicial) * 100) : 100;

  return (
    <div style={{
      border: `1.5px solid ${terminado ? "#9AC2A8" : "#8FB4D9"}`,
      background: terminado ? "#EAF6EF" : "#EEF4FB",
      borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 12.5,
    }}>
      {terminado ? (
        <div style={{ color: "#1E6B3C", fontWeight: 700 }}>
          ✅ {totalInicial} imagen(es) sincronizada(s)
        </div>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <div style={{ color: "#1B4A7A", fontWeight: 700 }}>
              📤 Sincronizando imágenes pendientes… {sincronizadas} de {totalInicial}
            </div>
            <div style={{ color: "#1B4A7A", fontWeight: 700 }}>{progreso}%</div>
          </div>
          <div style={{ height: 7, borderRadius: 5, background: "#d6e4f2", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progreso}%`, background: "#2d6a4f", borderRadius: 5, transition: "width 0.4s ease" }} />
          </div>
          {tardando && (
            <div style={{ marginTop: 7, color: "#854F0B", fontSize: 11.5 }}>
              ⏳ Esto está tardando más de lo normal — probablemente por la conexión. Puedes seguir usando la app mientras tanto, no hace falta esperar acá; sigue intentando solo, de a poco.
            </div>
          )}
          {atascadas.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <button onClick={() => setVerDetalle(p => !p)}
                style={{ background: "#fff", border: "1px solid #b7791f", color: "#854F0B", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                {verDetalle ? "Ocultar" : "Ver"} {atascadas.length} foto(s) con problemas
              </button>
              {verDetalle && (
                <div style={{ marginTop: 8 }}>
                  {atascadas.map(foto => (
                    <div key={foto.id} style={{ background: "#fff", border: "1px solid #f0d9b5", borderRadius: 8, padding: "8px 10px", marginBottom: 6, fontSize: 11.5 }}>
                      <div style={{ fontWeight: 700 }}>{foto.codigo_vivienda} — {foto.campo === "vivienda" ? "foto de la vivienda" : "foto del carné"}</div>
                      <div style={{ color: "#6c757d", marginTop: 2 }}>{foto.ultimoError || "Motivo desconocido"}</div>
                      <div style={{ color: "#6c757d", fontSize: 10.5 }}>{foto.intentosFallidos} intentos fallidos</div>
                      <button onClick={() => descartarFoto(foto)} disabled={descartando === foto.id}
                        style={{ marginTop: 5, background: "#fff", border: "1px solid #b3261e", color: "#b3261e", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700, cursor: descartando === foto.id ? "not-allowed" : "pointer" }}>
                        {descartando === foto.id ? "Descartando…" : "🗑️ Descartar esta foto"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}